import * as React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import EqualizerIcon from '@mui/icons-material/Equalizer'
import AirplaneTicketIcon from '@mui/icons-material/AirplaneTicket'
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person';
import { Badge, Fab, Tooltip, useTheme } from '@mui/material';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AuthContext from '../../context/auth';
import { useNavigate } from 'react-router-dom';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import LayoutBackgroundImage from './layout-background.png'
import useAxios from 'axios-hooks'
import moment from 'moment';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import FeedbackIcon from '@mui/icons-material/Feedback';
import PhoneIcon from '@mui/icons-material/Phone';
import TelegramIcon from '@mui/icons-material/Telegram';
import SosIcon from '@mui/icons-material/Sos';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { APP_NAME, APP_CONTACTS, APP_DOMAIN } from '@next-pontos/settings';
import ShareIcon from '@mui/icons-material/Share';
import { enqueueSnackbar } from 'notistack';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

export function Layout({children}: {children: React.ReactNode}) {
  const [open, setOpen] = React.useState(false);
  const { me, signOut } = React.useContext(AuthContext)
  const navigate = useNavigate()
  const theme = useTheme()

  const [payments, getPayments] = useAxios('/payment', {
    manual: true,
    autoCancel: false
  })

  const [orders, getOrders] = useAxios('/order', {
    manual: true,
    autoCancel: false
  })

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const onLogoutClick = React.useCallback(() => {
    signOut(() => {
      window.location.href = '/'
    })
  }, [signOut, navigate])
  
  const activeListItemStyles = React.useCallback((url: string) => {
    let basePathName = `/${window.location.pathname.split('/')[1]}`
    
    return url.includes(basePathName) ? {
      color: theme.palette.primary.main
    }: {}
  }, [theme])

  React.useEffect(() => {
    if (me) {
      if (me.is_first_login) {
        window.location.href = '/bem-vindo'
      }
    }
  }, [me])

  React.useEffect(() => {
    if (me?.role === 'ADMIN' && !payments.loading) {
      getPayments()
      getOrders()
    }
  }, [me?.role])
  
  return (
    <React.Fragment>
      <Box sx={{ display: 'flex' }}>
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 5,
                ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" display="flex" flex={1}>
              Bem-vindo(a) à {APP_NAME}, {me?.name}!
            </Typography>
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'flex-end',
                flexDirection: 'column'
              }}
            >
              <Typography variant="caption" noWrap component="div" display="flex" >
                <PhoneIcon fontSize='small' style={{marginRight: 10}}/> {`+${APP_CONTACTS.whatsapp.slice(0, 2)} ${APP_CONTACTS.whatsapp.slice(2, 4)} ${APP_CONTACTS.whatsapp.slice(4, 9)}-${APP_CONTACTS.whatsapp.slice(9, 13)}`}
              </Typography>
              <Typography variant="caption" noWrap component="div" display="flex" >
                <TelegramIcon fontSize='small' style={{marginRight: 10}}/> {`@${APP_CONTACTS.telegram}`}
              </Typography>
            </div>
          </Toolbar>
        </AppBar>
        <Drawer variant="permanent" open={open}>
          <DrawerHeader>
            <div style={{
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <img alt="logo" src="/logo.png" height={50} />
            </div>
            {open && (<IconButton onClick={handleDrawerClose}>
              {!open ? <MenuIcon /> : <ChevronLeftIcon />}
            </IconButton>)}
          </DrawerHeader>
          <Divider />
          <List>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  ...activeListItemStyles('/home')
                }}
                href={'/home'}
              >
              <Tooltip title='Home'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <EqualizerIcon style={activeListItemStyles('/home')}/>
                </ListItemIcon>
                </Tooltip>
                <ListItemText primary='Home' sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  ...activeListItemStyles('/minhas-ofertas')
                }}
                href={'/minhas-ofertas'}
              >
              <Tooltip title='Minhas Ofertas'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {
                    me?.bids.filter(item => item.status === 'ACCEPTED').length ? (
                      <Badge color="success" badgeContent={me?.bids.filter(item => item.status === 'ACCEPTED').length}>
                        <ChecklistRtlIcon style={activeListItemStyles('/minhas-ofertas')} />
                      </Badge>
                    ) : (
                      <ChecklistRtlIcon style={activeListItemStyles('/minhas-ofertas')} />
                    )
                  }
                </ListItemIcon>
                </Tooltip>
                <ListItemText primary='Minhas Ofertas' sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  ...activeListItemStyles('/meus-pagamentos')
                }}
                href={'/meus-pagamentos'}
              >
                <Tooltip title='Meus Pagamentos'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <PaymentsIcon style={activeListItemStyles('/meus-pagamentos')} />
                </ListItemIcon>
                </Tooltip>
                <ListItemText primary='Meus Pagamentos' sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  ...activeListItemStyles('/minhas-contas')
                }}
                href={'/minhas-contas'}
              >

              <Tooltip title='Minhas Contas'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <PeopleIcon style={activeListItemStyles('/minhas-contas')} />
                </ListItemIcon>
                </Tooltip>
                <ListItemText primary='Minhas Contas' sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  ...activeListItemStyles('/meu-perfil')
                }}
                href={'/meu-perfil'}
              >

              <Tooltip title='Meu Perfil'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {
                    !me?.pix_key ? (
                      <Badge color="error" badgeContent={"!"}>
                        <PersonIcon style={activeListItemStyles('/meu-perfil')} />
                      </Badge>
                    ) : (
                      <PersonIcon style={activeListItemStyles('/meu-perfil')} />
                    )
                  }
                </ListItemIcon>
                </Tooltip>
                <ListItemText primary='Meu Perfil' sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          {
            (me?.role === 'ADMIN' || me?.role === 'SELLER') && (
              <React.Fragment>
                <List>
                {me.role === 'ADMIN' && (<ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...activeListItemStyles('/dashboard')
                      }}
                      href={'/dashboard'}
                    >
                    <Tooltip title='Dashboard'>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        <EqualizerIcon style={activeListItemStyles('/dashboard')} />
                      </ListItemIcon>
                    </Tooltip>
                      <ListItemText primary='Dashboard' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>)}
                  {me.role === 'ADMIN' && (<ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...activeListItemStyles('/usuarios')
                      }}
                      href={'/usuarios'}
                    >
                    <Tooltip title='Usuários'>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        <PeopleIcon style={activeListItemStyles('/usuarios')} />
                      </ListItemIcon>
                    </Tooltip>
                      <ListItemText primary='Usuários' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>)}
                  {(me.role === 'ADMIN' || me.role === 'SELLER') && (<ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...activeListItemStyles('/ofertas')
                      }}
                      href={'/ofertas'}
                    >
                    <Tooltip title='Ofertas'>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        <ChecklistRtlIcon style={activeListItemStyles('/ofertas')} />
                      </ListItemIcon>
                      </Tooltip>
                      <ListItemText primary='Ofertas' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>)}
                  <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...activeListItemStyles('/emissoes')
                      }}
                      href={'/emissoes'}
                    >
                    <Tooltip title='Emissões'>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                      { orders?.data?.find((item: {confirmation: string, created_at: Date}) => item.confirmation === 'PENDING' && moment(item.created_at).isBefore(moment().subtract(48, 'hours'))) ? (
                        <Badge color="error" badgeContent={"!"}>
                        <AirplaneTicketIcon style={activeListItemStyles('/emissoes')} />
                        </Badge>
                      ) : (
                        <AirplaneTicketIcon style={activeListItemStyles('/emissoes')} />
                      )}
                      </ListItemIcon>
                      </Tooltip>
                      <ListItemText primary='Emissões' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>
                  {me.role === 'ADMIN' && (<ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...activeListItemStyles('/financeiro')
                      }}
                      href={'/financeiro'}
                    >
                    <Tooltip title='Financeiro'>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        { payments?.data?.find((item: {status: string}) => item.status === 'LATE') ? (
                        <Badge color="warning" badgeContent={"!"}>
                          <AccountBalanceIcon style={activeListItemStyles('/pagamentos')} />
                        </Badge>
                      ) : (
                        <AccountBalanceIcon style={activeListItemStyles('/pagamentos')} />
                      )}
                      </ListItemIcon>
                      </Tooltip>
                      <ListItemText primary='Financeiro' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>)}
                  {/* {me.role === 'ADMIN' && (<ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...activeListItemStyles('/pagamentos')
                      }}
                      href={'/pagamentos'}
                    >
                    <Tooltip title='Pagamentos'>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        { payments?.data?.find((item: {status: string}) => item.status === 'LATE') ? (
                        <Badge color="warning" badgeContent={"!"}>
                          <PaymentsIcon style={activeListItemStyles('/pagamentos')} />
                        </Badge>
                      ) : (
                        <PaymentsIcon style={activeListItemStyles('/pagamentos')} />
                      )}
                      </ListItemIcon>
                      </Tooltip>
                      <ListItemText primary='Pagamentos' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>)} */}
                  {me.role === 'ADMIN' && (<ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...activeListItemStyles('/cartoes')
                      }}
                      href={'/cartoes'}
                    >
                    <Tooltip title='Cartões'>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        <CreditCardIcon style={activeListItemStyles('/cartoes')} />
                      </ListItemIcon>
                    </Tooltip>
                      <ListItemText primary='Cartões' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>)}
                  {me.role === 'ADMIN' && (<ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...activeListItemStyles('/programas')
                      }}
                      href={'/programas'}
                    >
                    <Tooltip title='Programas'>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        <LoyaltyIcon style={activeListItemStyles('/programas')} />
                      </ListItemIcon>
                    </Tooltip>
                      <ListItemText primary='Programas' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>)}
                  {me.role === 'ADMIN' && (<ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        ...activeListItemStyles('/chamados')
                      }}
                      href={'/chamados'}
                    >
                    <Tooltip title='Chamados'>
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        <SosIcon style={activeListItemStyles('/chamados')} />
                      </ListItemIcon>
                    </Tooltip>
                      <ListItemText primary='Chamados' sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>)}
                </List>
                <Divider />
              </React.Fragment>
            )
          }
          <List
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {APP_CONTACTS.feedback_form_link ? (<ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
                href={APP_CONTACTS.feedback_form_link}
                target="_blank"
              >

              <Tooltip title='Feedback'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <FeedbackIcon />
                </ListItemIcon>
                </Tooltip>
                <ListItemText primary='Feedback' sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>) : null}
            <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                ...activeListItemStyles('/ajuda')
              }}
              href={'/ajuda'}
            >
              <Tooltip title='Ajuda'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <QuestionAnswerIcon style={activeListItemStyles('/ajuda')} />
                </ListItemIcon>
                </Tooltip>
                <ListItemText primary='Ajuda' sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
                onClick={onLogoutClick}
              >
              <Tooltip title='Logout'>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon></Tooltip>
                <ListItemText primary='Logout' sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
            <div style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'end',
              position: 'relative'
            }}>
              <img alt="logo" src="/logo-square.png" height={50} />
            </div>
          </List>
        </Drawer>
        <Box component="main" sx={{ 
          width: '100%',
          // backgroundImage: `url(logo.png)`, backgroundPosition: 'center', backgroundSize: '80%', backgroundRepeat: 'no-repeat', 
          height: 'calc(100vh - 64px)',
          }}>
          <DrawerHeader />
          {children}
        </Box>
      </Box>
      <Fab variant="extended" color='primary' sx={{
        position: 'absolute',
        right: 15,
        bottom: 15
      }} onClick={() => {
        navigator.clipboard.writeText(`${APP_DOMAIN}/cadastro?invite_code=${me?.invite_code}`)

        enqueueSnackbar('Link de indicação copiado com sucesso!', {
          variant: 'success'
        })
      }}>
        <ShareIcon sx={{ mr: 1 }} />
          Indicar
      </Fab>
    </React.Fragment>
  );
}
